// src/lib/fehlerprotokoll.js
// --- FEHLERPROTOKOLL -----------------------------------------------------------
// Aus dem Feldtest: "Wir mussten die App drei- bis fuenfmal schliessen und neu
// oeffnen." Warum, wusste niemand - es wurde nirgends etwas aufgezeichnet.
//
// Ab jetzt:
//   - Jeder Fehler landet zuerst auf dem Geraet (localStorage, die letzten 30).
//   - Sobald Netz da ist, geht er an die Supabase-Tabelle public.app_fehler.
//     Was nicht ankommt, bleibt liegen und wird spaeter nachgesendet.
//   - Veraltete Programmdateien nach einem Update (ChunkLoadError,
//     "Unexpected token '<'") werden erkannt: dann einmal sauber neu laden.
//
// Grundregel: Diese Datei darf NIE selbst einen Fehler werfen. Ein
// Fehlerprotokoll, das die App abstuerzen laesst, waere schlimmer als keins.

import { supabase, withTimeout } from './supabase';
import { APP_VERSION } from './version';
import { speicherIstVoll } from './warteschlange';

const SPEICHER_SCHLUESSEL = 'etb-fehlerprotokoll';
const MAX_EINTRAEGE = 30;
const MAX_STACK = 4000;
const MAX_MELDUNG = 1000;
const MAX_GERAET = 300;
const DOPPELT_FENSTER_MS = 10000;

const NEULADEN_SCHLUESSEL = 'etb-chunk-neuladen-zeit';
const NEULADEN_SPERRE_MS = 60000;

// --- Hilfsfunktionen -----------------------------------------------------------
function kuerzen(text, max) {
  try {
    const s = text == null ? '' : String(text);
    return s.length > max ? s.slice(0, max) + ' …[gekuerzt]' : s;
  } catch {
    return '';
  }
}

function meldungAus(fehler) {
  try {
    if (fehler == null) return 'Unbekannter Fehler';
    if (typeof fehler === 'string') return fehler;
    if (fehler.message) return (fehler.name && fehler.name !== 'Error' ? fehler.name + ': ' : '') + fehler.message;
    try { return JSON.stringify(fehler); } catch { return String(fehler); }
  } catch {
    return 'Unbekannter Fehler';
  }
}

// Seite ohne Anmelde-Schluessel: Beim Passwort-Reset steht im Hash
// "#access_token=...". Der gehoert nicht ins Fehlerprotokoll.
function aktuelleSeite() {
  try {
    const hash = window.location.hash || '';
    if (hash && !/token|type=recovery/i.test(hash)) return kuerzen(hash, 200);
    return kuerzen(window.location.pathname, 200);
  } catch {
    return '';
  }
}

function lesenRoh() {
  try {
    const roh = localStorage.getItem(SPEICHER_SCHLUESSEL);
    const liste = roh ? JSON.parse(roh) : [];
    return Array.isArray(liste) ? liste : [];
  } catch {
    return [];
  }
}

function schreiben(liste) {
  try {
    localStorage.setItem(SPEICHER_SCHLUESSEL, JSON.stringify(liste.slice(-MAX_EINTRAEGE)));
  } catch {
    // Speicher voll oder gesperrt - dann eben nicht. Nie werfen.
  }
}

// --- Oeffentliche Funktionen ---------------------------------------------------
export function fehlerLesen() {
  return lesenRoh();
}

// Gleiche Meldung innerhalb von 10 s nur einmal - sonst flutet ein Fehler,
// der bei jedem Neuzeichnen kommt, in Sekunden das ganze Protokoll.
const zuletztGemeldet = new Map();

export function fehlerMelden(fehler, zusatz = {}) {
  try {
    const meldung = kuerzen(meldungAus(fehler), MAX_MELDUNG);
    const art = zusatz.art || 'fehler';
    const jetzt = Date.now();
    const schluessel = art + '|' + meldung;
    const vorher = zuletztGemeldet.get(schluessel);
    if (vorher && jetzt - vorher < DOPPELT_FENSTER_MS) return;
    zuletztGemeldet.set(schluessel, jetzt);
    if (zuletztGemeldet.size > 100) zuletztGemeldet.clear();

    let stack = '';
    try { stack = (fehler && fehler.stack) ? String(fehler.stack) : ''; } catch { stack = ''; }
    if (zusatz.stack) stack = (stack ? stack + '\n--- Komponenten ---\n' : '') + String(zusatz.stack);

    let geraet = '';
    try { geraet = navigator.userAgent; } catch { geraet = ''; }

    const eintrag = {
      lid: jetzt.toString(36) + Math.random().toString(36).slice(2, 8), // nur lokal
      zeit: new Date(jetzt).toISOString(),
      meldung,
      stack: kuerzen(stack, MAX_STACK),
      app_version: APP_VERSION,
      seite: kuerzen(zusatz.seite || aktuelleSeite(), 200),
      geraet: kuerzen(geraet, MAX_GERAET),
      user_id: zusatz.userId || null,
      art,
      gesendet: false,
    };

    const liste = lesenRoh();
    liste.push(eintrag);
    schreiben(liste);

    // Fire and forget - der Aufrufer wartet nie auf das Senden.
    fehlerNachsenden();
  } catch {
    // Nie werfen.
  }
}

let sendetGerade = false;

export function fehlerNachsenden() {
  try {
    if (sendetGerade) return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    const offen = lesenRoh().filter(e => e && !e.gesendet);
    if (offen.length === 0) return;
    sendetGerade = true;

    (async () => {
      try {
        let userId = null;
        if (offen.some(e => !e.user_id)) {
          try {
            const { data } = await withTimeout(supabase.auth.getSession(), 5000, 'Sitzung lesen');
            userId = data?.session?.user?.id || null;
          } catch { userId = null; }
        }

        const zeilen = offen.map(e => ({
          zeit: e.zeit,
          user_id: e.user_id || userId,
          app_version: e.app_version,
          seite: e.seite,
          meldung: e.meldung,
          stack: e.stack,
          geraet: e.geraet,
          art: e.art,
        }));

        // Kein .select() dahinter: So reicht eine reine Einfuege-Regel (RLS).
        const { error } = await withTimeout(
          supabase.from('app_fehler').insert(zeilen), 20000, 'Fehlerbericht senden');
        if (error) return; // bleibt liegen, naechster Versuch spaeter

        // Neu lesen: Waehrend des Sendens koennen neue Eintraege dazugekommen sein.
        const gesendeteIds = new Set(offen.map(e => e.lid));
        const liste = lesenRoh().map(e => (e && gesendeteIds.has(e.lid) ? { ...e, gesendet: true } : e));
        schreiben(liste);
      } catch {
        // Kein Netz o. ae. - Eintraege bleiben ungesendet liegen.
      } finally {
        sendetGerade = false;
      }
    })();
  } catch {
    sendetGerade = false;
  }
}

// --- Veraltete Programmdateien nach einem Update -------------------------------
export function istChunkFehler(fehler) {
  try {
    if (!fehler) return false;
    if (fehler.name === 'ChunkLoadError') return true;
    const text = typeof fehler === 'string' ? fehler : String(fehler.message || '');
    return /Loading chunk|Loading CSS chunk|Unexpected token '<'|Failed to fetch dynamically imported module/i.test(text);
  } catch {
    return false;
  }
}

// Speichert, was noch in den Eingabefeldern haengt, und laedt dann neu.
// Hoechstens einmal pro Minute - sonst droht eine Neulade-Schleife, wenn der
// Fehler nach dem Neuladen immer noch da ist.
export function nachUpdateNeuLaden() {
  try {
    const jetzt = Date.now();
    let letztes = 0;
    try { letztes = Number(sessionStorage.getItem(NEULADEN_SCHLUESSEL)) || 0; } catch { letztes = 0; }
    if (jetzt - letztes < NEULADEN_SPERRE_MS) return false;
    // Liegen Eingaben nur im Arbeitsspeicher (Geraet voll), wuerde Neuladen sie loeschen.
    if (speicherIstVoll()) return false;
    try { sessionStorage.setItem(NEULADEN_SCHLUESSEL, String(jetzt)); } catch { /* egal */ }

    try { window.dispatchEvent(new CustomEvent('etb-jetzt-speichern')); } catch { /* egal */ }
    setTimeout(() => {
      try { window.location.reload(); } catch { /* egal */ }
    }, 400);
    return true;
  } catch {
    return false;
  }
}

// --- Globale Fehler abfangen ---------------------------------------------------
let installiert = false;

export function globaleFehlerFangen() {
  try {
    if (installiert || typeof window === 'undefined') return;
    installiert = true;

    const behandeln = (fehler) => {
      try {
        const meldung = meldungAus(fehler);
        // Harmloses Browser-Rauschen, kein echter Fehler.
        if (/ResizeObserver loop/i.test(meldung)) return;
        const chunk = istChunkFehler(fehler);
        fehlerMelden(fehler, { art: chunk ? 'chunk' : 'global' });
        if (chunk) nachUpdateNeuLaden();
      } catch { /* nie werfen */ }
    };

    window.addEventListener('error', (e) => {
      try { behandeln(e && (e.error || e.message)); } catch { /* nie werfen */ }
    });
    window.addEventListener('unhandledrejection', (e) => {
      try { behandeln(e && e.reason); } catch { /* nie werfen */ }
    });

    // Nachsenden, sobald wieder Netz da ist - und einmal kurz nach dem Start.
    window.addEventListener('online', () => { fehlerNachsenden(); });
    setTimeout(() => { fehlerNachsenden(); }, 5000);
  } catch {
    // Nie werfen.
  }
}
